import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { generatePath, Link, useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@/components/ui/empty'
import { Lock03Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { CreatorOnlyNotice } from '@/features/marquee/components/creator-only-notice'
import { MarqueeEventForm } from '@/features/marquee/components/event-form'
import type { EventFormValues } from '@/features/marquee/form-schemas'
import {
	eventQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	eventPlaceValue,
	eventStateToLabelKey,
	isMarketManagerAllowed,
	timeOfDay
} from '@/features/marquee/utils'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

function MarqueeEventEdit() {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()
	// Editing is creator work (FR51). The "Edit event" button is already hidden
	// for a facilitator, so only a URL reaches this screen, and the permission is
	// asked first: nothing about the event is fetched for someone who may not
	// edit it.
	const { data: operator } = useQuery(operatorQueryOptions())
	const isAllowedToEdit = isMarketManagerAllowed(operator, 'updateEvent')

	const eventOptions = eventQueryOptions(id)
	const { data: event, isLoading } = useQuery({
		...eventOptions,
		enabled: eventOptions.enabled && isAllowedToEdit
	})

	const initialValues = useMemo<EventFormValues | null>(() => {
		if (!event) {
			return null
		}
		return {
			name: event.name,
			// The picker's value: the event's custom location when it has one,
			// its market otherwise.
			market: eventPlaceValue(
				event.location
					? { kind: 'location', id: event.location.id }
					: { kind: 'market', id: event.market.id }
			),
			date: event.date,
			startTime: timeOfDay(event.start_time),
			endTime: timeOfDay(event.end_time),
			format: event.format,
			captureMode: event.capture_mode,
			// Kept as-is, `null` included: an event created before the field
			// existed edits with nothing selected rather than being silently
			// rewritten to in-person.
			eventType: event.event_type,
			hashtag: event.hashtag,
			ctas: event.ctas ?? [],
			marketManagerIds: event.assigned_mms?.map((mm) => mm.id) ?? []
		}
	}, [event])

	// Answered before this page mounted (`MarqueeSessionRoute`) and before the
	// event even loads: the role is the permanent half of
	// the answer, and telling a facilitator that the event is locked would imply
	// that an unlocked one would let them in.
	if (!isAllowedToEdit) {
		return (
			<>
				<title>{t('marquee:page_title_edit')}</title>
				<CreatorOnlyNotice
					backLabel={t('marquee:edit_locked_back')}
					backTo={generatePath(
						routes.MARQUEE_EVENT_ATTENDEES.handle.fullPath,
						{ id: id ?? '' }
					)}
				/>
			</>
		)
	}

	if (isLoading || !event || !initialValues) {
		return (
			<div className='flex flex-1 items-center justify-center py-24'>
				<title>{t('marquee:page_title_edit')}</title>
				<Spinner size={24} />
			</div>
		)
	}

	// Editing is only valid while the event is in pre-work. The "Edit event"
	// button is already hidden once it's locked/closed, but the route stays
	// reachable directly — guard it here and explain why it's blocked.
	if (event.state !== 'pre_work') {
		return (
			<Empty className='flex-1 py-24'>
				<title>{t('marquee:page_title_edit')}</title>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<Lock03Icon />
					</EmptyMedia>
					<EmptyTitle>{t('marquee:edit_locked_title')}</EmptyTitle>
					<EmptyDescription>
						{t('marquee:edit_locked_description', {
							state: t(
								eventStateToLabelKey[event.state]
							).toLowerCase()
						})}
					</EmptyDescription>
				</EmptyHeader>
				<Button asChild size='sm' variant='secondaryOutline'>
					<Link
						to={generatePath(
							routes.MARQUEE_EVENT_ATTENDEES.handle.fullPath,
							{ id: id ?? '' }
						)}
					>
						{t('marquee:edit_locked_back')}
					</Link>
				</Button>
			</Empty>
		)
	}

	return (
		<>
			<title>{t('marquee:page_title_edit')}</title>
			<MarqueeEventForm
				currentLocation={
					event.location
						? {
								id: event.location.id,
								name: event.location.name,
								marketId: event.market.id,
								marketName: event.market.name,
								timeZone: event.time_zone
							}
						: undefined
				}
				editEventId={id}
				initialValues={initialValues}
				timeZone={event.time_zone}
			/>
		</>
	)
}

export { MarqueeEventEdit }
